import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getEventByCode, subscribeToEventBouts } from "../firebase/services";

export default function SpectateJoin() {
  const [code, setCode] = useState("");
  const [event, setEvent] = useState(null);
  const [bouts, setBouts] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLookup = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setError(""); setLoading(true);
    try {
      const found = await getEventByCode(code.trim().toUpperCase());
      if (!found) { setError("Event not found. Check your code."); setLoading(false); return; }
      setEvent(found);
    } catch (err) { console.error(err); setError("Something went wrong."); }
    setLoading(false);
  };

  useEffect(() => {
    if (!event) return;
    const unsub = subscribeToEventBouts(event.id, setBouts);
    return unsub;
  }, [event]);

  const activeBouts = bouts.filter(b => b.status !== "confirmed");
  const completedBouts = bouts.filter(b => b.status === "confirmed");

  return (
    <div className="flex flex-col items-center min-h-screen p-6">
      <Link to="/" className="text-gray-600 text-sm mb-8 no-underline hover:text-gray-400 self-start">← Back</Link>

      {!event ? (
        <>
          <div className="text-center mb-8">
            <div className="text-4xl mb-2">📺</div>
            <h1 className="font-display text-2xl font-bold">Spectate</h1>
            <p className="text-gray-500 text-sm mt-1">Enter the event code to watch live bouts</p>
          </div>
          <form onSubmit={handleLookup} className="w-full max-w-xs flex flex-col gap-4">
            <label htmlFor="spectate-event-code" className="text-sm text-gray-300">Event code</label>
            <input id="spectate-event-code" aria-invalid={Boolean(error)} aria-describedby={error ? "spectate-code-error" : undefined} value={code} onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 6))} placeholder="ABC123" maxLength={6}
              className="w-full p-4 bg-dark-800 border border-dark-500 rounded-xl text-white text-center font-display text-3xl font-extrabold tracking-[0.3em] outline-none focus:border-gray-400 uppercase" />
            {error && <div id="spectate-code-error" role="alert" className="text-red-400 text-sm text-center bg-red-900 bg-opacity-20 rounded-lg p-2">{error}</div>}
            <button type="submit" disabled={loading || code.length < 4}
              className="w-full p-4 bg-dark-600 border border-dark-500 text-white font-display text-lg font-bold rounded-xl tracking-wide disabled:opacity-40 cursor-pointer hover:bg-dark-700">
              {loading ? "Finding..." : "Find Event"}
            </button>
          </form>
        </>
      ) : (
        <div className="w-full">
          <h1 className="font-display text-xl font-bold mb-4">{event.name}</h1>

          {activeBouts.length > 0 && (
            <div className="mb-5">
              <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-2">ACTIVE BOUTS</div>
              {activeBouts.map(b => (
                <button key={b.id} onClick={() => navigate(`/spectate/${event.id}/${b.id}`)}
                  className="w-full text-left bg-dark-700 border border-dark-500 rounded-xl p-4 mb-2 cursor-pointer hover:border-gray-600 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-display text-sm font-bold" style={{ color: "#EF9A9A" }}>{b.fighterRed}</span>
                      <span className="text-gray-600 text-xs">vs</span>
                      <span className="font-display text-sm font-bold" style={{ color: "#64B5F6" }}>{b.fighterBlue}</span>
                    </div>
                    <span className="text-xs font-bold px-2 py-1 rounded-lg bg-green-action text-white">LIVE</span>
                  </div>
                </button>
              ))}
            </div>
          )}

          {completedBouts.length > 0 && (
            <div>
              <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-2">COMPLETED</div>
              {completedBouts.map(b => (
                <button key={b.id} onClick={() => navigate(`/spectate/${event.id}/${b.id}`)}
                  className="w-full text-left bg-dark-700 border border-dark-500 rounded-xl p-3 mb-2 cursor-pointer hover:border-gray-600 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-display text-sm font-bold" style={{ color: "#EF9A9A" }}>{b.fighterRed}</span>
                      <span className="text-gray-600 text-xs">vs</span>
                      <span className="font-display text-sm font-bold" style={{ color: "#64B5F6" }}>{b.fighterBlue}</span>
                    </div>
                    {b.confirmedResult && (
                      <div className="flex items-center gap-2">
                        <span className="font-display font-bold" style={{ color: "#E53935" }}>{b.confirmedResult.scoreA}</span>
                        <span className="text-gray-600 text-xs">–</span>
                        <span className="font-display font-bold" style={{ color: "#1E88E5" }}>{b.confirmedResult.scoreB}</span>
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          {bouts.length === 0 && <p className="text-gray-600 text-sm text-center py-8">No bouts yet in this event.</p>}

          <button onClick={() => { setEvent(null); setCode(""); setBouts([]); }}
            className="w-full p-2 mt-4 bg-transparent border-none text-gray-600 text-sm cursor-pointer hover:text-gray-400">← Different event</button>
        </div>
      )}
    </div>
  );
}
